import { useState, FormEvent } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactForm() {
  const { t } = useLanguage();
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [charCount, setCharCount] = useState(0);

  const requirementOptions = [
    t('技術導入與試產', 'Technology Implementation & Pilot Production', '技術導入・試験生産'),
    t('產品與材料採購', 'Product & Material Procurement', '製品・材料調達'),
    t('區域經銷合作', 'Regional Distribution Partnership', '地域販売代理協力'),
    t('品牌 ESG / 低碳產品專案', 'Brand ESG / Low-Carbon Product Project', 'ブランドESG・低炭素製品プロジェクト'),
    t('政府 / 示範場域合作', 'Government / Demonstration Site Cooperation', '政府・実証サイト協力'),
    t('其他需求', 'Other Inquiries', 'その他のご要望'),
  ];

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value ?? '';
    if (message.length > 500) return;

    setStatus('submitting');
    const data = new URLSearchParams();
    const fields = ['name', 'company', 'title', 'email', 'phone', 'country', 'requirement', 'material', 'scenario', 'message'];
    fields.forEach((f) => {
      const el = form.elements.namedItem(f) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null;
      if (el) data.append(f, el.value);
    });

    try {
      const res = await fetch('https://readdy.ai/api/form/d7nln882li4t868hk21g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const inputClass =
    'w-full px-4 py-3 rounded-xl border border-neutral-border bg-white text-neutral-dark text-[1rem] placeholder-neutral-mid/60 focus:outline-none focus:border-brand-green focus:ring-2 focus:ring-brand-green/15 transition-all duration-200';
  const labelClass = 'block text-[1rem] font-semibold text-neutral-dark mb-1.5';

  return (
    <section className="section-padding bg-white">
      <div className="max-w-screen-xl mx-auto px-4 md:px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[1rem] font-semibold text-brand-green uppercase tracking-widest mb-3">
              {t('聯絡表單', 'Contact Form', 'お問い合わせフォーム')}
            </p>
            <h2
              className="font-bold text-neutral-dark"
              style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)', lineHeight: '1.4' }}
            >
              {t('送出諮詢需求', 'Submit Your Inquiry', 'お問い合わせを送信')}
            </h2>
          </div>

          {status === 'success' && (
            <div className="mb-8 p-6 rounded-2xl bg-brand-green-light border border-brand-green/30 text-center">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-brand-green mx-auto mb-4">
                <i className="ri-check-line text-white text-2xl"></i>
              </div>
              <h3 className="font-bold text-neutral-dark text-[1.25rem] mb-2">
                {t('諮詢需求已送出！', 'Inquiry Submitted!', 'お問い合わせを送信しました！')}
              </h3>
              <p className="text-neutral-mid text-[1.0625rem]">
                {t(
                  '感謝您的聯絡，聚量團隊將於 3 個工作天內回覆您。',
                  'Thank you for contacting us. The PolyQ team will reply within 3 business days.',
                  'お問い合わせありがとうございます。聚量チームは3営業日以内にご返信いたします。'
                )}
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="mb-6 p-4 rounded-xl bg-[#FDECEA] border border-[#C0392B]/20 flex items-center gap-3">
              <i className="ri-error-warning-line text-[#C0392B] text-xl flex-shrink-0"></i>
              <p className="text-[#C0392B] text-[1rem]">
                {t(
                  '送出失敗，請稍後再試或直接 Email 聯絡我們。',
                  'Submission failed. Please try again or contact us directly by email.',
                  '送信に失敗しました。しばらくしてから再試行するか、メールで直接ご連絡ください。'
                )}
              </p>
            </div>
          )}

          <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
            {/* Row 1: Name + Company */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="name" className={labelClass}>
                  {t('姓名', 'Full Name', 'お名前')} <span className="text-[#C0392B]">*</span>
                </label>
                <input
                  id="name" name="name" type="text" required
                  placeholder={t('您的姓名', 'Your name', 'お名前')}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="company" className={labelClass}>
                  {t('公司名稱', 'Company Name', '会社名')} <span className="text-[#C0392B]">*</span>
                </label>
                <input
                  id="company" name="company" type="text" required
                  placeholder={t('公司或組織名稱', 'Company or organization name', '会社名または組織名')}
                  className={inputClass}
                />
              </div>
            </div>

            {/* Row 2: Title + Email */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="title" className={labelClass}>
                  {t('職稱', 'Job Title', '役職')}
                </label>
                <input
                  id="title" name="title" type="text"
                  placeholder={t('您的職稱', 'Your job title', '役職名')}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="email" className={labelClass}>
                  Email <span className="text-[#C0392B]">*</span>
                </label>
                <input
                  id="email" name="email" type="email" required
                  placeholder="your@email.com"
                  className={inputClass}
                />
              </div>
            </div>

            {/* Row 3: Phone + Country */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="phone" className={labelClass}>
                  {t('電話', 'Phone', '電話番号')}
                </label>
                <input
                  id="phone" name="phone" type="tel"
                  placeholder="+886 ..."
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="country" className={labelClass}>
                  {t('所在國家 / 地區', 'Country / Region', '国・地域')}
                </label>
                <input
                  id="country" name="country" type="text"
                  placeholder={t('例如：台灣、日本、韓國', 'e.g. Taiwan, Japan, Korea', '例：台湾、日本、韓国')}
                  className={inputClass}
                />
              </div>
            </div>

            {/* Requirement type */}
            <div>
              <label htmlFor="requirement" className={labelClass}>
                {t('合作需求類型', 'Cooperation Type', '協力内容の種類')} <span className="text-[#C0392B]">*</span>
              </label>
              <div className="relative">
                <select
                  id="requirement" name="requirement" required defaultValue=""
                  className={`${inputClass} appearance-none pr-10 cursor-pointer`}
                >
                  <option value="" disabled>
                    {t('請選擇合作需求類型', 'Select cooperation type', '協力内容の種類を選択')}
                  </option>
                  {requirementOptions.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none w-5 h-5 flex items-center justify-center">
                  <i className="ri-arrow-down-s-line text-neutral-mid text-lg"></i>
                </div>
              </div>
            </div>

            {/* Material type */}
            <div>
              <label htmlFor="material" className={labelClass}>
                {t('目前使用的塑膠材料或產品類型', 'Current Plastic Material or Product Type', '現在使用しているプラスチック材料または製品の種類')}
              </label>
              <input
                id="material" name="material" type="text"
                placeholder={t('例如：rPET、rPP、ABS、混合塑料…', 'e.g. rPET, rPP, ABS, mixed plastics…', '例：rPET・rPP・ABS・混合プラスチック…')}
                className={inputClass}
              />
            </div>

            {/* Scenario */}
            <div>
              <label htmlFor="scenario" className={labelClass}>
                {t('預計導入的應用場景', 'Intended Application Scenario', '導入予定の応用シーン')}
              </label>
              <input
                id="scenario" name="scenario" type="text"
                placeholder={t('例如：造粒、射出成型、包材、農業資材…', 'e.g. compounding, injection molding, packaging, agricultural materials…', '例：造粒・射出成形・包装材・農業資材…')}
                className={inputClass}
              />
            </div>

            {/* Message */}
            <div>
              <label htmlFor="message" className={labelClass}>
                {t('訊息內容', 'Message', 'メッセージ内容')} <span className="text-[#C0392B]">*</span>
              </label>
              <textarea
                id="message" name="message" required rows={5} maxLength={500}
                placeholder={t(
                  '請描述您的需求、問題或希望討論的方向…',
                  'Please describe your needs, questions, or topics you would like to discuss…',
                  'ご要望・ご質問・ご相談内容をご記入ください…'
                )}
                className={`${inputClass} resize-none`}
                onChange={(e) => setCharCount(e.target.value.length)}
              />
              <div className="flex justify-between items-center mt-1.5">
                <p className={`text-[0.875rem] ${charCount > 480 ? 'text-[#C0392B]' : 'text-neutral-mid'}`}>
                  {charCount > 500 ? t('已超過 500 字元限制', 'Exceeded 500 character limit', '500文字の制限を超えました') : ''}
                </p>
                <p className={`text-[0.875rem] ${charCount > 480 ? 'text-[#C0392B]' : 'text-neutral-mid'}`}>
                  {charCount} / 500
                </p>
              </div>
            </div>

            {/* Submit */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={status === 'submitting' || charCount > 500}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-10 py-4 rounded-full bg-brand-green text-white text-[1.0625rem] font-semibold hover:bg-brand-green-dark disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                {status === 'submitting' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    {t('送出中…', 'Submitting…', '送信中…')}
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i>
                    {t('送出諮詢需求', 'Submit Inquiry', 'お問い合わせを送信')}
                  </>
                )}
              </button>
              <p className="mt-3 text-neutral-mid text-[0.9375rem]">
                <i className="ri-time-line text-brand-green mr-1"></i>
                {t('通常於 3 個工作天內回覆', 'Usually replied within 3 business days', '通常3営業日以内にご返信いたします')}
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
